/**
 * Optimized Website Script
 * 
 * This script contains various UI effects and animations organized by functionality.
 * Each function is conditionally loaded only when necessary elements are present on the page.
 */

// Main initialization function that runs when DOM is ready
document.addEventListener("DOMContentLoaded", function() {
    // Define all module initializers
    const modules = {
        initScrollUpButton,
        initVideoHandlers,
        initParallaxBlock,
        initHorizontalScrollSection,
        initSelectedProjects,
        initImageBlurText,
        initColumnAnimation,
        initProjectOptions,
        initGallerySlider,
        initProjectsListHover,
        initQuotesListHover,
        initClientsMarquee,
        initHomeCarouselCaption,
        initHomeCarouselDrag,
        initMobileMenuClose,
        // initFooterZIndex — DESACTIVADO: era del footer "reveal" antiguo; dejaba
        // el footer en z-index -10 (detrás del contenido) y no se podía pinchar.
        initStickyHeader // HABILITADO - implementación inteligente
    };

    // Initialize each module conditionally
    Object.values(modules).forEach(module => {
        try {
            module();
        } catch (error) {
            console.error(`Error initializing module: ${error.message}`);
        }
    });

    // Inicializar manejo de resize para horizontal-section
    handleHorizontalSectionResize();
});

/**
 * Menú móvil — el botón "Cerrar" del overlay (como en Figma) cierra el menú,
 * replicando el comportamiento del toggle hamburguesa.
 */
function initMobileMenuClose() {
    const siteNavigation = document.getElementById('site-navigation');
    if (!siteNavigation) return;
    const closeButton = siteNavigation.querySelector('.menu-close');
    const toggleButton = siteNavigation.querySelector('.menu-toggle');
    if (!closeButton) return;
    closeButton.addEventListener('click', function() {
        siteNavigation.classList.remove('toggled');
        if (toggleButton) {
            toggleButton.setAttribute('aria-expanded', 'false');
        }
    });
}

/**
 * Home — sincroniza el texto bajo el hero (estilo canadacanada) con la imagen
 * del carrusel más cercana al centro del viewport. Cada slide lleva data-name y
 * data-url (permalink de su proyecto), de modo que el nombre es un enlace.
 *
 * El carrusel se pausa al pasar el ratón por encima para poder leer y pulsar
 * el nombre sin que la imagen destacada cambie bajo el cursor.
 */
function initHomeCarouselCaption() {
    const track = document.querySelector('.home-hero-track');
    const caption = document.querySelector('.home-featured-name[data-dynamic]');
    if (!track || !caption) return;
    const slides = Array.from(track.querySelectorAll('.home-hero-slide'));
    if (!slides.length) return;

    const captionLink = caption.querySelector('.home-featured-name__link') || caption;
    const marquee = track.closest('.home-hero-marquee') || track;
    // Slide sobre la que está el cursor: manda sobre la slide central.
    let hoveredSlide = null;

    function applySlide(slide) {
        if (!slide) return;
        const name = slide.getAttribute('data-name') || '';
        const url = slide.getAttribute('data-url') || '';
        if (captionLink.textContent !== name) {
            captionLink.textContent = name;
        }
        if (url) {
            if (captionLink.getAttribute('href') !== url) {
                captionLink.setAttribute('href', url);
            }
        } else if (captionLink.hasAttribute('href')) {
            captionLink.removeAttribute('href');
        }
    }

    function findCenteredSlide() {
        const centerX = window.innerWidth / 2;
        let closest = null;
        let closestDist = Infinity;
        for (const slide of slides) {
            const rect = slide.getBoundingClientRect();
            const dist = Math.abs((rect.left + rect.width / 2) - centerX);
            if (dist < closestDist) {
                closestDist = dist;
                closest = slide;
            }
        }
        return closest;
    }

    function update() {
        applySlide(hoveredSlide || findCenteredSlide());
        requestAnimationFrame(update);
    }
    requestAnimationFrame(update);

    // Pausa: el hover sobre el carrusel o sobre el propio nombre detiene el bucle.
    function pauseMarquee() {
        if (!pausaHabilitada) return;
        track.classList.add('is-paused');
    }
    function resumeMarquee() {
        track.classList.remove('is-paused');
    }

    // El hero ocupa la parte alta de la página, así que al cargar la home el
    // cursor suele estar ya encima y el carrusel arrancaría parado. La pausa
    // nace desarmada y se arma en cuanto el ratón se mueve fuera del carrusel
    // o sale de él: hay que salir y volver a entrar para que pause.
    let pausaHabilitada = false;

    function armarPausa() {
        pausaHabilitada = true;
    }

    document.addEventListener('mousemove', function(evento) {
        if (pausaHabilitada) return;
        const cursorSobreElCarrusel = marquee.contains(evento.target) || caption.contains(evento.target);
        if (!cursorSobreElCarrusel) {
            armarPausa();
        }
    }, { passive: true });

    function habilitarPausaAlSalir() {
        armarPausa();
        hoveredSlide = null;
        resumeMarquee();
    }

    marquee.addEventListener('mouseenter', pauseMarquee);
    marquee.addEventListener('mouseleave', habilitarPausaAlSalir);
    caption.addEventListener('mouseenter', pauseMarquee);
    caption.addEventListener('mouseleave', habilitarPausaAlSalir);

    slides.forEach(function(slide) {
        slide.addEventListener('mouseenter', function() {
            // Mientras la pausa siga desarmada, el nombre lo marca la imagen
            // central, no la que casualmente tenga el cursor encima al cargar.
            if (!pausaHabilitada) return;
            hoveredSlide = slide;
        });
    });
}

/**
 * Home — arrastre del carrusel del hero.
 *
 * Por defecto el carrusel lo mueve una animación CSS (`marqueeSlideLeft`), que
 * funciona aunque el JS no llegue a cargar. Una animación CSS no se puede
 * desplazar con el ratón, así que este módulo la sustituye: marca la pista con
 * `js-carrusel-manual` (el CSS deja entonces de animarla) y pasa a mover el
 * `transform` fotograma a fotograma. Así el avance automático y el arrastre
 * comparten la misma posición y no se pelean.
 *
 * Si este módulo falla, la clase no se aplica y sigue mandando la animación CSS.
 */
function initHomeCarouselDrag() {
    const pista = document.querySelector('.home-hero-track');
    if (!pista) return;
    const carrusel = pista.closest('.home-hero-marquee') || pista;

    // La secuencia de imágenes va triplicada en el HTML para que el bucle no
    // dé saltos: un ciclo completo es un tercio del ancho total.
    let anchoCiclo = pista.scrollWidth / 3;
    if (!anchoCiclo) return;

    // Misma velocidad que tenía la animación CSS: un ciclo cada 55 segundos.
    const SEGUNDOS_POR_CICLO = 55;
    const movimientoReducido = window.matchMedia('(prefers-reduced-motion: reduce)');

    let desplazamiento = 0;
    let arrastrando = false;
    let xInicial = 0;
    let desplazamientoInicial = 0;
    let inercia = 0;           // px/s que aporta el impulso al soltar
    let ultimaX = 0;
    let ultimoInstante = 0;
    let distanciaArrastrada = 0;

    pista.classList.add('js-carrusel-manual');

    // El ancho depende de las imágenes, que se cargan en diferido: hay que
    // recalcularlo cuando terminan de cargar y cuando cambia el tamaño.
    function recalcularAncho() {
        const nuevo = pista.scrollWidth / 3;
        if (nuevo) anchoCiclo = nuevo;
    }
    window.addEventListener('resize', recalcularAncho);
    pista.querySelectorAll('img').forEach(function(imagen) {
        if (!imagen.complete) imagen.addEventListener('load', recalcularAncho, { once: true });
    });

    function ajustarAlCiclo(valor) {
        // Mantiene el desplazamiento dentro de un ciclo, en cualquier sentido.
        if (anchoCiclo <= 0) return valor;
        return ((valor % anchoCiclo) + anchoCiclo) % anchoCiclo - anchoCiclo;
    }

    function pintar() {
        pista.style.transform = 'translate3d(' + desplazamiento + 'px, 0, 0)';
    }

    let instanteAnterior = performance.now();

    function avanzar(instante) {
        const segundos = Math.min((instante - instanteAnterior) / 1000, 0.1);
        instanteAnterior = instante;

        if (!arrastrando) {
            // El avance automático se detiene al pasar el ratón por encima y
            // cuando el sistema pide movimiento reducido, pero el arrastre
            // manual sigue disponible en ambos casos.
            const parado = pista.classList.contains('is-paused') || movimientoReducido.matches;
            const velocidadAutomatica = parado ? 0 : anchoCiclo / SEGUNDOS_POR_CICLO;

            desplazamiento -= velocidadAutomatica * segundos;

            // Impulso al soltar: se va apagando hasta fundirse con el automático.
            if (inercia !== 0) {
                desplazamiento += inercia * segundos;
                inercia *= Math.pow(0.002, segundos); // ~99.8% de frenada por segundo
                if (Math.abs(inercia) < 8) inercia = 0;
            }

            desplazamiento = ajustarAlCiclo(desplazamiento);
            pintar();
        }

        requestAnimationFrame(avanzar);
    }
    requestAnimationFrame(avanzar);

    function empezarArrastre(evento) {
        // Solo botón principal del ratón; el táctil y el lápiz entran siempre.
        if (evento.pointerType === 'mouse' && evento.button !== 0) return;
        arrastrando = true;
        distanciaArrastrada = 0;
        inercia = 0;
        xInicial = evento.clientX;
        ultimaX = evento.clientX;
        ultimoInstante = performance.now();
        desplazamientoInicial = desplazamiento;
        carrusel.classList.add('is-dragging');
        try {
            carrusel.setPointerCapture(evento.pointerId);
        } catch (e) {
            // Si el navegador no deja capturar, los eventos siguen llegando igual.
        }
    }

    function moverArrastre(evento) {
        if (!arrastrando) return;
        const recorrido = evento.clientX - xInicial;
        distanciaArrastrada = Math.max(distanciaArrastrada, Math.abs(recorrido));

        // Velocidad instantánea, para el impulso al soltar.
        const ahora = performance.now();
        const intervalo = (ahora - ultimoInstante) / 1000;
        if (intervalo > 0.004) {
            inercia = (evento.clientX - ultimaX) / intervalo;
            ultimaX = evento.clientX;
            ultimoInstante = ahora;
        }

        desplazamiento = ajustarAlCiclo(desplazamientoInicial + recorrido);
        pintar();
    }

    function terminarArrastre() {
        if (!arrastrando) return;
        arrastrando = false;
        carrusel.classList.remove('is-dragging');
        instanteAnterior = performance.now();
        // Se limita el impulso para que un gesto brusco no dispare el carrusel.
        inercia = Math.max(-2500, Math.min(2500, inercia));
    }

    carrusel.addEventListener('pointerdown', empezarArrastre);
    carrusel.addEventListener('pointermove', moverArrastre);
    carrusel.addEventListener('pointerup', terminarArrastre);
    carrusel.addEventListener('pointercancel', terminarArrastre);
    carrusel.addEventListener('pointerleave', terminarArrastre);

    // Arrastrar sobre una imagen no debe abrir su proyecto. Se anula el clic
    // solo si hubo recorrido real, para no romper la pulsación normal.
    carrusel.addEventListener('click', function(evento) {
        if (distanciaArrastrada > 6) {
            evento.preventDefault();
            evento.stopPropagation();
            distanciaArrastrada = 0;
        }
    }, true);

    // El navegador intenta arrastrar la imagen como fichero: estorba al gesto.
    carrusel.addEventListener('dragstart', function(evento) {
        evento.preventDefault();
    });
}

/**
 * Smooth scroll function used by multiple modules
 */
function smoothScrollToTop(duration = 1000) {
    const start = window.scrollY;
    const startTime = performance.now();

    function scrollStep(currentTime) {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeInOut = progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;

        window.scrollTo(0, start * (1 - easeInOut));

        if (progress < 1) {
            requestAnimationFrame(scrollStep);
        }
    }

    requestAnimationFrame(scrollStep);
}

/**
 * Module 1: Scroll Up Button
 * Adds a button that appears when scrolling down and scrolls back to top when clicked
 */
function initScrollUpButton() {
    const scrollUp = document.getElementById("scrollUp");
    if (!scrollUp) return; // Exit if element doesn't exist

    // Show/hide button based on scroll position
    window.addEventListener("scroll", function() {
        if (window.scrollY > window.innerHeight) {
            scrollUp.classList.add("show");
        } else {
            scrollUp.classList.remove("show");
        }
    });

    // Add click handler
    scrollUp.addEventListener("click", function() {
        smoothScrollToTop(1000);
    });
}

/**
 * Module 2: Video Player Handlers
 * Manages YouTube video embeds with click-to-play functionality
 */
function initVideoHandlers() {
    const videoWrappers = document.querySelectorAll('.video-wrapper-wrapper');
    if (videoWrappers.length === 0) return; // Exit if no videos present

    videoWrappers.forEach(videoWrapper => {
        videoWrapper.addEventListener('click', function(e) {
            e.preventDefault();

            const iframeContainer = this.querySelector('.youtube-iframe');
            const iframe = iframeContainer?.querySelector('iframe');
            const videoCover = this.querySelector('.video-cover');
            const playButton = this.querySelector('.play-button');

            if (!iframe) return;

            // Add autoplay parameter to iframe src
            let iframeSrc = iframe.getAttribute('src');
            if (!iframeSrc.includes('autoplay=1')) {
                iframeSrc += (iframeSrc.includes('?') ? '&' : '?') + 'autoplay=1';
                iframe.setAttribute('src', iframeSrc);
            }

            // Hide cover and play button, show iframe
            if (videoCover) videoCover.style.visibility = 'hidden';
            if (playButton) playButton.style.visibility = 'hidden';
            iframe.style.display = 'block';

            // Function to maintain 16:9 aspect ratio
            const adjustHeight = () => {
                const width = iframe.clientWidth;
                const newHeight = width * (9 / 16);
                iframe.style.height = `${newHeight}px`;
                videoWrapper.style.height = `${newHeight}px`;
            };

            // Adjust height when window is resized
            window.addEventListener('resize', adjustHeight);
            
            // Initial height adjustment
            adjustHeight();
        });
    });
}

/**
 * Module 3: Parallax Block
 * Creates parallax scrolling effect for block with fixed title
 */
function initParallaxBlock() {
    const parallaxBlock = document.querySelector('.parallax-block');
    const fixedTitle = document.querySelector('.fixed-title');
    const columns = document.querySelector('.parallax-content');
    
    if (!parallaxBlock || !fixedTitle || !columns) return;

    function handleScroll() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const blockOffsetTop = parallaxBlock.getBoundingClientRect().top + window.pageYOffset;
        const blockHeight = parallaxBlock.offsetHeight;
        const blockBottom = blockOffsetTop + blockHeight;
        const viewportHeight = window.innerHeight;

        // Different parallax speeds
        const parallaxFactorTitle = 1.8;
        const parallaxFactorColumns = 0.8;

        // Responsive offset adjustment
        const extraOffset = window.innerWidth >= 780 ? 120 : 10;

        const translateYTitle = (scrollTop - blockOffsetTop) * parallaxFactorTitle + extraOffset;
        const translateYColumns = (scrollTop - blockOffsetTop) * parallaxFactorColumns;

        // Apply transformations when block is in viewport
        if (scrollTop > blockOffsetTop - viewportHeight && scrollTop < blockBottom) {
            fixedTitle.style.transform = `translateX(-50%) translateY(${translateYTitle}px)`;
            columns.style.transform = `translateY(${translateYColumns}px)`;
        } else {
            fixedTitle.style.transform = `translateX(-50%) translateY(${blockHeight - columnsHeight}px)`;
            columns.style.transform = `translateY(${blockHeight - columnsHeight}px)`;
        }
    }

    window.addEventListener('resize', handleScroll);
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call
}

/**
 * Module 5: Selected Projects Animation
 * Animates projects section with scroll-triggered effects
 */
function initSelectedProjects() {
    const projectsSection = document.querySelector('.selected_projects');
    if (!projectsSection) return;

    const titleElement = projectsSection.querySelector('header');
    const projectsList = projectsSection.querySelector('.selected-projects-list');
    const projectItems = projectsSection.querySelectorAll('.selected-item');
    
    // Check if we're on mobile/tablet (< 1080px) to disable animations
    const isMobile = () => window.innerWidth < 1080;
    
    // Track section visibility and resize state
    let isSectionVisible = false;
    let currentIsMobile = isMobile();
    
    // Function to set mobile styles
    function setMobileStyles() {
        if (titleElement) {
            titleElement.style.opacity = '1';
            titleElement.style.position = 'static';
            titleElement.style.zIndex = 'auto';
            titleElement.style.transition = 'none';
        }
        
        if (projectsList) {
            projectsList.style.position = 'relative';
            projectsList.style.zIndex = 'auto';
            projectsList.style.transition = 'none';
        }
        
        projectItems.forEach(item => {
            item.style.opacity = '1';
            item.style.transform = 'none';
            item.style.position = 'relative';
            item.style.transition = 'none';
        });
    }
    
    // Function to set desktop initial styles
    function setDesktopInitialStyles() {
        if (titleElement) {
            titleElement.style.opacity = '1';
            titleElement.style.position = 'absolute';
            titleElement.style.zIndex = '100';
            titleElement.style.transition = 'opacity 0.8s ease';
        }
        
        if (projectsList) {
            projectsList.style.position = 'relative';
            projectsList.style.zIndex = '2';
            projectsList.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
        }
        
        projectItems.forEach(item => {
            item.style.opacity = '0';
            item.style.transform = 'translateY(200%)';
            item.style.position = 'relative';
            item.style.transition = 'none';
        });
        
        // Reset section visibility for desktop
        isSectionVisible = false;
    }
    
    // Initialize based on current screen size
    if (isMobile()) {
        setMobileStyles();
    } else {
        setDesktopInitialStyles();
    }
    
    // Scroll handler
    function handleScroll() {
        // Re-check if mobile on scroll (for resize handling)
        if (isMobile()) {
            setMobileStyles();
            return;
        }
        
        const rect = projectsSection.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        // Check if section is visible
        const isVisible = rect.top < windowHeight * 0.8 && rect.bottom > 0;
        
        if (isVisible) {
            // First time visible - activate transitions
            if (!isSectionVisible) {
                projectItems.forEach(item => {
                    item.style.transition = 'opacity 1s ease, transform 1.2s ease';
                });
                isSectionVisible = true;
            }
            
            // Calculate scroll progress
            const scrollRange = windowHeight * 0.6;
            const scrollProgress = Math.min(1, Math.max(0, 
                (windowHeight * 0.8 - rect.top) / scrollRange
            ));
            
            // Fade title gradually
            if (titleElement) {
                titleElement.style.opacity = Math.max(0.1, 1 - (scrollProgress * 0.7));
            }
            
            // Animate project items with delay
            projectItems.forEach((item, index) => {
                const startThreshold = 0.4;
                const delay = index * 0.15;
                
                const itemProgress = Math.max(0, Math.min(1, 
                    (scrollProgress - startThreshold - delay) * 2
                ));
                
                item.style.opacity = itemProgress;
                const translateY = (1 - itemProgress) * 280;
                item.style.transform = `translateY(${translateY}%)`;
            });
        } else if (rect.top > windowHeight) {
            // Reset when completely out of view
            if (titleElement) {
                titleElement.style.opacity = '1';
            }
            
            projectItems.forEach(item => {
                item.style.opacity = '0';
                item.style.transform = 'translateY(300%)';
            });
        }
    }
    
    // Handle window resize
    function handleResize() {
        const newIsMobile = isMobile();
        
        // Check if screen size category changed
        if (currentIsMobile !== newIsMobile) {
            currentIsMobile = newIsMobile;
            
            if (newIsMobile) {
                // Switched to mobile
                setMobileStyles();
            } else {
                // Switched to desktop - reinitialize
                setDesktopInitialStyles();
                // Trigger scroll handler to set up animations
                setTimeout(handleScroll, 100);
            }
        } else if (!newIsMobile) {
            // Still desktop, just handle scroll
            handleScroll();
        }
    }
    
    // Add event listeners
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    window.addEventListener('load', handleScroll);
    
    // Initial call
    handleScroll();
}


function initImageBlurText() {
    // Mobile resolution detection function
    function isMobileResolution() {
        return window.innerWidth <= 768;
    }

    // Exit early if mobile resolution detected
    if (isMobileResolution()) {
        console.log('Mobile resolution detected - Image blur text effect disabled');
        return;
    }

    const section = document.querySelector('.image-blur-text');
    if (!section) return;
    
    const titleEl = section.querySelector('.title');
    const titleText = section.querySelector('.title-text');
    const imageContainer = section.querySelector('.image');
    const imageEl = section.querySelector('.image img');

    if (!titleEl || !titleText || !imageContainer || !imageEl) return;
    
    // Initialize
    titleText.classList.remove('negative-effect');
    let imageUrl = '';
    let isEffectActive = false;
    
    // Store image URL
    imageEl.onload = function() {
        imageUrl = imageEl.src;
    };
    
    if (imageEl.complete) {
        imageUrl = imageEl.src;
    }
    
    // Initial positioning
    imageContainer.style.bottom = '-150%';
    imageEl.style.width = '25vw';
    imageEl.style.maxWidth = '280px';
    
    // Add transitions
    imageContainer.style.transition = 'bottom 0.8s cubic-bezier(0.25, 0.1, 0.25, 1)';
    imageEl.style.transition = 'width 0.8s cubic-bezier(0.25, 0.1, 0.25, 1), max-width 0.8s cubic-bezier(0.25, 0.1, 0.25, 1)';
    
    // MEJORADO: Configurar background para que coincida exactamente con la imagen
    function setupBackgroundImage() {
        if (!imageUrl) return;

        const containerRect = imageContainer.getBoundingClientRect();
        const textRect = titleText.getBoundingClientRect();
        const imageRect = imageEl.getBoundingClientRect();

        // Calcular el tamaño exacto de la imagen visible
        const bgWidth = imageRect.width;
        const bgHeight = imageRect.height;

        // Calcular el offset exacto entre la imagen y el texto
        const offsetX = imageRect.left - textRect.left;
        const offsetY = imageRect.top - textRect.top;

        // Configurar background para que coincida exactamente con la imagen
        titleText.style.backgroundImage = `url(${imageUrl})`;
        titleText.style.backgroundSize = `${bgWidth}px ${bgHeight}px`;
        titleText.style.backgroundPosition = `${offsetX}px ${offsetY}px`;
        titleText.style.backgroundRepeat = 'no-repeat';
    }

    // MEJORADO: Activar efecto más gradualmente
    function activateEffect() {
        if (isEffectActive || !imageUrl) return;
        
        isEffectActive = true;
        setupBackgroundImage();
        
        titleText.style.transition = 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)';
        titleText.classList.add('negative-effect');
    }

    // MEJORADO: Desactivar efecto
    function deactivateEffect() {
        if (!isEffectActive) return;
        
        isEffectActive = false;
        titleText.style.transition = 'all 0.6s ease-out';
        titleText.classList.remove('negative-effect');

        setTimeout(() => {
            titleText.style.backgroundImage = 'none';
        }, 200);
    }
    
    function updateAnimation() {
        // Check if resolution became mobile during resize
        if (isMobileResolution()) {
            return;
        }

        const sectionRect = section.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        // Salir si la sección está muy fuera de la ventana
        if (sectionRect.bottom < -200 || sectionRect.top > windowHeight + 200) {
            if (isEffectActive) {
                deactivateEffect();
            }
            return;
        }

        const titleRect = titleEl.getBoundingClientRect();
        const titleNormalizedPos = titleRect.top / windowHeight;

        // Puntos de animación más amplios para mejor detección
        const startRisePoint = 0.7;     // Comenzar antes
        const completeRisePoint = 0.1;  // Terminar más tarde
        const startExpandPoint = 0.7;
        const completeExpandPoint = 0.1;

        // Animación de subida de imagen
        const riseProgress = Math.min(1, Math.max(0, 
            (startRisePoint - titleNormalizedPos) / (startRisePoint - completeRisePoint)
        ));
        
        const bottomPos = -150 + riseProgress * 150;
        imageContainer.style.bottom = `${Math.min(0, Math.max(-150, bottomPos))}%`;

        // Animación de expansión de imagen
        const expandProgress = Math.min(1, Math.max(0, 
            (startExpandPoint - titleNormalizedPos) / (startExpandPoint - completeExpandPoint)
        ));
        
        const widthPercent = 25 + expandProgress * 75;
        imageEl.style.width = `${Math.min(100, Math.max(25, widthPercent))}vw`;

        if (expandProgress >= 0.3) {
            imageEl.style.maxWidth = 'none';
        } else {
            imageEl.style.maxWidth = '1280px';
        }

        // MEJORADO: Detección de superposición más permisiva
        const imageRect = imageContainer.getBoundingClientRect();
        const textRect = titleText.getBoundingClientRect();

        const hasOverlap = !(
            imageRect.right < textRect.left - 50 ||   // Mayor tolerancia
            imageRect.left > textRect.right + 50 ||
            imageRect.bottom < textRect.top - 50 ||
            imageRect.top > textRect.bottom + 50
        );

        // MEJORADO: Solo activar cuando la animación esté 100% completa
        const isAnimationComplete = riseProgress >= 0.99 && expandProgress >= 0.99;
        const shouldActivate = hasOverlap && isAnimationComplete;

        if (shouldActivate && !isEffectActive) {
            // Esperar un momento adicional para asegurar que la animación CSS haya terminado
            setTimeout(() => {
                if (hasOverlap && riseProgress >= 0.99 && expandProgress >= 0.99) {
                    activateEffect();
                }
            }, 100);
        } else if (!shouldActivate && isEffectActive) {
            deactivateEffect();
        }

        // Solo actualizar background si el efecto está activo
        if (isEffectActive && hasOverlap) {
            setupBackgroundImage();
        }

        // Posicionamiento del título
        if (riseProgress > 0) {
            titleEl.style.top = 'auto';
            titleEl.style.bottom = `${-50 + 80 * (1 - riseProgress)}px`;
        } else {
            titleEl.style.top = '50px';
            titleEl.style.bottom = 'auto';
        }
    }
    
    // Optimize scroll performance
    let animationFrame = null;
    
    function handleScroll() {
        if (isMobileResolution()) return;
        
        if (animationFrame) {
            cancelAnimationFrame(animationFrame);
        }
        
        animationFrame = requestAnimationFrame(() => {
            updateAnimation();
            animationFrame = null;
        });
    }
    
    window.addEventListener('scroll', handleScroll);
    
    window.addEventListener('resize', function() {
        if (isMobileResolution()) return;
        
        updateAnimation();
        // Reconfigurar background si está activo
        if (isEffectActive) {
            setupBackgroundImage();
        }
    });
    
    // Llamadas iniciales
    updateAnimation();
    
    // Backup: verificar después de un momento por si las imágenes cargan tarde
    setTimeout(updateAnimation, 100);
}

// Initialize when DOM loads
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initImageBlurText);
} else {
    initImageBlurText();
}


let horizontalTrigger; // variable en ámbito superior

// Función helper para detectar dispositivos móviles
function isMobileDevice() {
    return window.innerWidth <= 1024;
}

function initHorizontalScrollSection() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

    const horizontalSection = document.querySelector('.horizontal-section');
    const siteHeader = document.querySelector('.site-header');

    // Si es mobile, no inicializar ScrollTrigger y forzar grid view
    if (isMobileDevice()) {
        if (horizontalSection && !horizontalSection.classList.contains('grid')) {
            horizontalSection.classList.add('grid');
        }
        return;
    }

    if (!horizontalSection || horizontalSection.classList.contains('grid') || !siteHeader) return;

    // Si ya había uno, lo matamos antes de recrear
    if (horizontalTrigger) {
        horizontalTrigger.kill();
        horizontalTrigger = null;
        // eliminar manualmente el spacer sobrante (por si acaso)
        const spacer = horizontalSection.parentNode.querySelector('.pin-spacer');
        if (spacer) spacer.remove();
    }

    gsap.registerPlugin(ScrollTrigger);

    const containerToScroll = horizontalSection.querySelector('.container');
    if (!containerToScroll) return;

    horizontalTrigger = ScrollTrigger.create({
        trigger: horizontalSection,
        pin: true,
        scrub: 1,
        start: 'top top',
        end: () => `+=${containerToScroll.scrollWidth - window.innerWidth}`,
        invalidateOnRefresh: true,
        anticipatePin: 1,
        // onToggle: self => siteHeader.classList.toggle('header-is-fixed', self.isActive), // Removido para evitar conflictos
        // onRefresh: self => siteHeader.classList.toggle('header-is-fixed', self.isActive), // Removido para evitar conflictos
        // animación de x
        animation: gsap.to(containerToScroll, {
            x: -(containerToScroll.scrollWidth - window.innerWidth),
            ease: 'none'
        })
    });

    // Agregar funcionalidad de arrastre con mouse
    initHorizontalDrag(horizontalSection, containerToScroll);
}

// Nueva función para manejar el arrastre del slider horizontal
function initHorizontalDrag(section, container) {
    let isDragging = false;
    let startX = 0;
    let scrollLeft = 0;
    let velocity = 0;
    let lastX = 0;
    let lastTime = 0;

    section.addEventListener('mousedown', (e) => {
        isDragging = true;
        startX = e.pageX;
        lastX = e.pageX;
        lastTime = Date.now();
        scrollLeft = window.pageYOffset;
        velocity = 0;
        section.style.cursor = 'grabbing';
        section.style.userSelect = 'none';
    });

    section.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        e.preventDefault();

        const x = e.pageX;
        const walk = (startX - x) * 2; // Multiplicar para más sensibilidad
        const currentTime = Date.now();
        const deltaTime = currentTime - lastTime;

        if (deltaTime > 0) {
            velocity = (lastX - x) / deltaTime;
        }

        lastX = x;
        lastTime = currentTime;

        // Calcular nuevo scroll basado en el movimiento
        let targetScroll = scrollLeft + walk;

        // Obtener los límites del scroll
        const startScroll = horizontalTrigger.start;
        const endScroll = horizontalTrigger.end;
        const maxScroll = endScroll - startScroll;

        // Implementar wrap-around instantáneo durante el arrastre
        if (targetScroll > endScroll) {
            // Si vamos más allá del final, hacer salto al inicio
            const overflow = targetScroll - endScroll;
            targetScroll = startScroll + overflow;
            scrollLeft = startScroll;
            startX = x;
        } else if (targetScroll < startScroll) {
            // Si vamos antes del inicio, hacer salto al final
            const underflow = startScroll - targetScroll;
            targetScroll = endScroll - underflow;
            scrollLeft = endScroll;
            startX = x;
        }

        // Aplicar el scroll con límites estrictos
        window.scrollTo(0, Math.max(startScroll, Math.min(endScroll, targetScroll)));
    });

    const endDrag = () => {
        if (!isDragging) return;
        isDragging = false;
        section.style.cursor = 'grab';
        section.style.userSelect = '';

        // Aplicar inercia basada en la velocidad
        if (Math.abs(velocity) > 0.5) {
            const momentumScroll = velocity * 200; // Factor de momento
            const currentScroll = window.pageYOffset;
            const targetScroll = currentScroll + momentumScroll;
            const startScroll = horizontalTrigger.start;
            const endScroll = horizontalTrigger.end;

            gsap.to(window, {
                scrollTo: {
                    y: Math.max(startScroll, Math.min(endScroll, targetScroll)),
                    autoKill: true
                },
                duration: 0.8,
                ease: 'power2.out'
            });
        }
    };

    section.addEventListener('mouseup', endDrag);
    section.addEventListener('mouseleave', endDrag);

    // Prevenir selección de texto y arrastre de imágenes
    section.addEventListener('dragstart', (e) => e.preventDefault());

    // Cursor visual
    section.style.cursor = 'grab';
}

function initProjectOptions() {
    const options = document.querySelectorAll('.option');
    const horizontalSection = document.querySelector('.horizontal-section');
    const viewSelector = document.querySelector('.view-selector');

    // Ocultar view selector en mobile
    if (isMobileDevice()) {
        if (viewSelector) {
            viewSelector.style.display = 'none';
        }
        // Forzar grid view en mobile
        if (horizontalSection && !horizontalSection.classList.contains('grid')) {
            horizontalSection.classList.add('grid');
        }
        return;
    }

    if (options.length === 0) return;

    options.forEach((option, index) => {
        option.addEventListener('click', function() {
            // No hacer scroll to top en mobile
            if (!isMobileDevice()) {
                smoothScrollToTop(1000);
            }

            options.forEach(opt => opt.classList.remove('selected'));
            this.classList.add('selected');

            const panels = horizontalSection.querySelectorAll('.panel');

            if (horizontalSection && index === 1) {
                // Entramos en grid: animación de salida y luego cambio
                if (typeof gsap !== 'undefined') {
                    gsap.to(panels, {
                        opacity: 0,
                        scale: 0.9,
                        duration: 0.3,
                        stagger: 0.05,
                        ease: 'power2.in',
                        onComplete: () => {
                            horizontalSection.classList.add('grid');

                            if (horizontalTrigger) {
                                horizontalTrigger.kill();
                                horizontalTrigger = null;
                            }
                            const spacer = horizontalSection.parentNode.querySelector('.pin-spacer');
                            if (spacer) spacer.remove();

                            // Animación de entrada en grid
                            gsap.fromTo(panels,
                                { opacity: 0, scale: 0.9, y: 30 },
                                {
                                    opacity: 1,
                                    scale: 1,
                                    y: 0,
                                    duration: 0.5,
                                    stagger: 0.1,
                                    ease: 'power2.out'
                                }
                            );
                        }
                    });
                } else {
                    horizontalSection.classList.add('grid');
                    if (horizontalTrigger) {
                        horizontalTrigger.kill();
                        horizontalTrigger = null;
                    }
                    const spacer = horizontalSection.parentNode.querySelector('.pin-spacer');
                    if (spacer) spacer.remove();
                }

            } else if (horizontalSection && !isMobileDevice()) {
                // Salimos de grid: animación de salida y luego cambio
                if (typeof gsap !== 'undefined') {
                    // Fade out con escala hacia el centro
                    gsap.to(panels, {
                        opacity: 0,
                        scale: 0.85,
                        duration: 0.4,
                        stagger: {
                            each: 0.05,
                            from: 'end'
                        },
                        ease: 'power3.in',
                        onComplete: () => {
                            // Resetear transforms antes de cambiar de modo
                            gsap.set(panels, { clearProps: 'all' });

                            horizontalSection.classList.remove('grid');

                            // Pequeño delay para que el DOM se actualice
                            setTimeout(() => {
                                initHorizontalScrollSection();

                                // Animación de entrada en slider - desde la izquierda
                                gsap.fromTo(panels,
                                    {
                                        opacity: 0,
                                        x: -100
                                    },
                                    {
                                        opacity: 1,
                                        x: 0,
                                        duration: 0.6,
                                        stagger: 0.15,
                                        ease: 'power2.out'
                                    }
                                );
                            }, 100);
                        }
                    });
                } else {
                    horizontalSection.classList.remove('grid');
                    initHorizontalScrollSection();
                }
            }
        });
    });
}

// Función para manejar el resize de la ventana
function handleHorizontalSectionResize() {
    const horizontalSection = document.querySelector('.horizontal-section');
    const viewSelector = document.querySelector('.view-selector');
    
    if (!horizontalSection) return;

    if (isMobileDevice()) {
        // Mobile: ocultar selector y forzar grid
        if (viewSelector) {
            viewSelector.style.display = 'none';
        }
        if (!horizontalSection.classList.contains('grid')) {
            horizontalSection.classList.add('grid');
        }
        // Matar ScrollTrigger si existe
        if (horizontalTrigger) {
            horizontalTrigger.kill();
            horizontalTrigger = null;
        }
    } else {
        // Desktop: mostrar selector y permitir cambios
        if (viewSelector) {
            viewSelector.style.display = '';
        }
        // Si no está en grid, reiniciar ScrollTrigger
        if (!horizontalSection.classList.contains('grid')) {
            initHorizontalScrollSection();
        }
    }
}

// Listener para resize con debounce
let resizeTimeout;
window.addEventListener('resize', function() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(handleHorizontalSectionResize, 250);
});


/**
 * Module 9: Four Columns Animation
 * Animates columns with GSAP ScrollTrigger
 */
// Global variables for column animation management
let columnAnimationInstance = null;
let columnResizeHandler = null;
let currentTriggerSection = null;
let currentColumnsToAnimate = null;

// Global function to calculate dynamic height
function calculateColumnHeight() {
    if (!currentTriggerSection || !currentColumnsToAnimate) return;
    
    let maxHeight = 0;
    const screenWidth = window.innerWidth;
    const isTablet = screenWidth >= 782 && screenWidth < 1080;
    const columnsPerRow = isTablet ? 2 : 4;
    
    currentColumnsToAnimate.forEach((column, index) => {
        // Get column's natural height
        const columnHeight = column.offsetHeight;
        
        // Calculate staggered animation offset (starting from 90vh, ending at 0)
        const staggerDelay = index * 0.2;
        const animationProgress = Math.min(1, staggerDelay + 0.5); // Simulate animation progress
        const currentTransform = 90 * (1 - animationProgress); // vh units
        
        // Convert vh to pixels (90vh is the initial offset)
        const vhInPx = window.innerHeight / 100;
        const transformInPx = currentTransform * vhInPx;
        
        // Calculate total height needed for this column
        const totalColumnHeight = columnHeight + transformInPx;
        
        // For tablet layout (2 columns), need to account for rows
        if (isTablet) {
            const row = Math.floor(index / columnsPerRow);
            const heightWithRow = totalColumnHeight + (row * 60); // Add row spacing
            if (heightWithRow > maxHeight) {
                maxHeight = heightWithRow;
            }
        } else {
            // Desktop layout (4 columns)
            if (totalColumnHeight > maxHeight) {
                maxHeight = totalColumnHeight;
            }
        }
    });
    
    // Add 400px padding that wasn't being calculated
    const additionalPadding = isTablet ? 200 : 400; // Less padding for tablet
    maxHeight += additionalPadding;
    
    // Set minimum height to 80vh as requested
    const minHeightVh = 80;
    const minHeightPx = (minHeightVh * window.innerHeight) / 100;
    
    // Use the larger value between calculated height and minimum height
    const finalHeight = Math.max(maxHeight, minHeightPx);
    
    // Convert back to vh for consistent scaling
    const finalHeightVh = (finalHeight / window.innerHeight) * 100;
    
    // Apply dynamic height to the container
    currentTriggerSection.style.setProperty('--calculated-height', `${finalHeightVh}vh`);
    currentTriggerSection.classList.add('js-calculated-height');
    
    return finalHeightVh;
}

function initColumnAnimation() {
    // Check if GSAP is available
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
    
    // Select the section and columns
    currentTriggerSection = document.querySelector('.block-4-columns');
    if (!currentTriggerSection) return;
    
    // Get all column containers
    currentColumnsToAnimate = gsap.utils.toArray('.block-4-columns .column .column-container');
    if (currentColumnsToAnimate.length === 0) return;
    
    // Check if viewport is desktop (only above 1080px should have animation)
    const isDesktop = window.innerWidth >= 1080;
    
    // Clean up any existing animation
    if (columnAnimationInstance) {
        columnAnimationInstance.kill();
        columnAnimationInstance = null;
    }
    
    // Reset all column styles
    currentColumnsToAnimate.forEach(column => {
        gsap.set(column, { clearProps: "all" });
    });
    
    // Reset container styles
    currentTriggerSection.style.removeProperty('--calculated-height');
    currentTriggerSection.classList.remove('js-calculated-height');
    
    if (!isDesktop) return;
    
    // Calculate initial height
    const dynamicHeight = calculateColumnHeight();
    
    // Create the animation
    columnAnimationInstance = gsap.from(currentColumnsToAnimate, {
        y: '90vh',
        ease: "power3.out",
        duration: 1,
        stagger: 0.2,
        
        scrollTrigger: {
            trigger: currentTriggerSection,
            start: "top 75%",
            end: "center center",
            scrub: 5,
            onComplete: () => {
                // Recalculate height after animation completes
                calculateColumnHeight();
            }
        }
    });
    
    // Setup resize handler only once
    if (!columnResizeHandler) {
        let resizeTimeout;
        let wasDesktop = window.innerWidth >= 1080; // Track previous state
        
        columnResizeHandler = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                const currentIsDesktop = window.innerWidth >= 1080;
                
                // If screen size category changed, restart the entire system
                if (wasDesktop !== currentIsDesktop) {
                    initColumnAnimation();
                    wasDesktop = currentIsDesktop;
                } else if (currentIsDesktop) {
                    // If still desktop, just recalculate height and refresh
                    calculateColumnHeight();
                    ScrollTrigger.refresh();
                }
            }, 250);
        };
        window.addEventListener('resize', columnResizeHandler);
    }
}



/**
 * Module 10: Gallery Slider
 * Creates an infinite carousel/slider for gallery items with drag support
 */
function initGallerySlider() {
    var slider = document.querySelector('.gallery-slider');
    var items = document.querySelectorAll('.slider-item');

    if (!slider || items.length === 0) return;

    var originalItems = Array.from(items);

    // Con un número impar de fotos, al repetir la serie coincidían dos grandes
    // seguidas (la última y la primera de la siguiente vuelta). Se duplica la
    // serie para que sea par y la alternancia GRANDE + PEQUEÑA no se rompa.
    if (originalItems.length % 2 === 1) {
        originalItems.forEach(function(item) {
            slider.appendChild(item.cloneNode(true));
        });
        originalItems = Array.from(slider.querySelectorAll('.slider-item'));
    }

    var NUM_ORIGINALS = originalItems.length;

    // Clone 4 times (5 sets total) for infinite scroll buffer
    for (var c = 0; c < 4; c++) {
        originalItems.forEach(function(item) {
            slider.appendChild(item.cloneNode(true));
        });
    }

    var allItems = slider.querySelectorAll('.slider-item');

    // Assign even/odd classes
    allItems.forEach(function(item, index) {
        var posInSet = index % NUM_ORIGINALS;
        if (posInSet % 2 === 0) {
            item.classList.add('even');
            item.classList.remove('odd');
        } else {
            item.classList.add('odd');
            item.classList.remove('even');
        }
    });

    var currentIndex = 0;
    var isAnimating = false;
    var isDragging = false;

    // Measure layout: step between items AND base position of item 0
    // Must be measured without any transform applied
    slider.style.transition = 'none';
    slider.style.transform = 'none';
    void slider.offsetHeight;

    var itemStep = 0;
    var item0Base = 0;
    var edgeOffset = 0;

    function measureLayout() {
        var savedTx = slider.style.transform;
        slider.style.transition = 'none';
        slider.style.transform = 'none';
        void slider.offsetHeight;
        var r0 = allItems[0].getBoundingClientRect();
        var r1 = allItems[1].getBoundingClientRect();
        itemStep = r1.left - r0.left;
        item0Base = r0.left;
        // Calculate symmetric edge offset accounting for gaps
        var containerWidth = slider.closest('.gallery_block_line').getBoundingClientRect().width;
        var itemWidth = r0.width;
        var gap = itemStep - itemWidth;
        var fullItemsVisible = Math.floor((containerWidth - gap) / itemStep);
        var edgePx = (containerWidth - fullItemsVisible * itemStep - gap) / 2;
        edgeOffset = (itemWidth - edgePx) / itemStep;
        slider.style.transform = savedTx;
        void slider.offsetHeight;
    }

    measureLayout();

    // Calculate translateX to show item at given index
    // Symmetric edges: equal partial visibility on both sides
    function txForIndex(index) {
        return -(item0Base + index * itemStep + itemStep * edgeOffset);
    }

    // Start at middle set (set 2)
    currentIndex = NUM_ORIGINALS * 2;
    slider.style.transform = 'translateX(' + txForIndex(currentIndex) + 'px)';
    void slider.offsetHeight;
    slider.style.transition = 'transform 0.8s ease';

    function setPosition(index, animate) {
        slider.style.transition = animate ? 'transform 0.8s ease' : 'none';
        slider.style.transform = 'translateX(' + txForIndex(index) + 'px)';
        if (!animate) void slider.offsetHeight;
    }

    // Jump to equivalent position in the middle set without visual change
    function normalizeIndex() {
        var relative = ((currentIndex % NUM_ORIGINALS) + NUM_ORIGINALS) % NUM_ORIGINALS;
        var target = NUM_ORIGINALS * 2 + relative;
        if (currentIndex !== target) {
            currentIndex = target;
            setPosition(currentIndex, false);
        }
    }

    // Index from a raw translateX value
    function indexFromTx(tx) {
        return Math.round(-(tx + item0Base + itemStep * edgeOffset) / itemStep);
    }

    // === Desplazamiento continuo y suave (estilo casabien) + arrastre ===
    slider.style.transition = 'none';
    var contTx = txForIndex(NUM_ORIGINALS * 2);
    var autoSpeed = 0.6; // px por frame — velocidad del desplazamiento continuo
    var paused = false;
    var wrapDist = itemStep * NUM_ORIGINALS;
    var centerTx = txForIndex(NUM_ORIGINALS * 2);

    function wrapTx(tx) {
        while (tx < centerTx - wrapDist) tx += wrapDist;
        while (tx > centerTx + wrapDist) tx -= wrapDist;
        return tx;
    }

    function galleryLoop() {
        if (!isDragging && !paused) {
            contTx = wrapTx(contTx - autoSpeed);
            slider.style.transform = 'translateX(' + contTx + 'px)';
        }
        requestAnimationFrame(galleryLoop);
    }
    requestAnimationFrame(galleryLoop);

    // Pausa suave al pasar el ratón por encima
    slider.addEventListener('mouseenter', function() { paused = true; });
    slider.addEventListener('mouseleave', function() { paused = false; });

    // Resize: re-medir el layout
    var resizeTimer;
    window.addEventListener('resize', function() {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function() {
            measureLayout();
            centerTx = txForIndex(NUM_ORIGINALS * 2);
            wrapDist = itemStep * NUM_ORIGINALS;
            contTx = wrapTx(contTx);
        }, 250);
    });

    // === Arrastre (drag) — al soltar, el bucle continuo retoma desde donde quedó ===
    var dragStartX = 0;
    var dragPrevTx = 0;
    slider.style.cursor = 'grab';

    slider.addEventListener('mousedown', onDragStart);
    slider.addEventListener('touchstart', onDragStart, { passive: false });
    window.addEventListener('mousemove', onDrag);
    window.addEventListener('touchmove', onDrag, { passive: false });
    window.addEventListener('mouseup', onDragEnd);
    slider.addEventListener('touchend', onDragEnd);
    slider.addEventListener('dragstart', function(e) { e.preventDefault(); });

    function getPointerX(e) {
        return e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
    }

    function onDragStart(e) {
        e.preventDefault();
        isDragging = true;
        dragStartX = getPointerX(e);
        dragPrevTx = contTx;
        slider.style.cursor = 'grabbing';
    }

    function onDrag(e) {
        if (!isDragging) return;
        e.preventDefault();
        contTx = wrapTx(dragPrevTx + (getPointerX(e) - dragStartX));
        slider.style.transform = 'translateX(' + contTx + 'px)';
    }

    function onDragEnd() {
        if (!isDragging) return;
        isDragging = false;
        slider.style.cursor = 'grab';
    }
}

/**
 * Module 11: Projects List Hover Effect
 * Changes background image when hovering over project items
 */
function initProjectsListHover() {
    const projectsList = document.querySelector('.projects_list_block');
    const hoverItems = document.querySelectorAll('.hover-item');
    
    if (!projectsList || hoverItems.length === 0) return;
    
    // Set background image function
    function setBackground(element) {
        const thumbnailUrl = element.getAttribute('data-thumbnail');
        if (thumbnailUrl && thumbnailUrl !== 'undefined' && thumbnailUrl !== '') {
            projectsList.style.backgroundImage = `url('${thumbnailUrl}')`;
            projectsList.classList.add('has-background');
        }
    }
    
    // Set default background from last project instead of first
    if (hoverItems.length > 0) {
        setBackground(hoverItems[hoverItems.length - 1]);
    }
    
    // Add hover handlers to each item
    hoverItems.forEach(item => {
        item.addEventListener('mouseenter', function() {
            setBackground(this);
        });
    });
}

/**
 * Module: Quotes List Hover Effect
 * Changes background image when hovering over quotes in quotes_list_block
 */
function initQuotesListHover() {
    const quotesList = document.querySelector('.quotes_list_block');
    const hoverItems = document.querySelectorAll('.quotes_list_block .hover-item');
    
    if (!quotesList || hoverItems.length === 0) return;
    
    // Set background image function
    function setBackground(element) {
        const thumbnailUrl = element.getAttribute('data-thumbnail');
        if (thumbnailUrl && thumbnailUrl !== 'undefined' && thumbnailUrl !== '') {
            quotesList.style.backgroundImage = `url('${thumbnailUrl}')`;
            quotesList.classList.add('has-background');
        }
    }
    
    // Set default background from last quote instead of first
    if (hoverItems.length > 0) {
        setBackground(hoverItems[hoverItems.length - 1]);
    }
    
    // Add hover handlers to each item
    hoverItems.forEach(item => {
        item.addEventListener('mouseenter', function() {
            setBackground(this);
        });
    });
}

/**
 * Module: Clients Marquee Setup
 * Simple marquee without complex positioning - pure CSS solution
 */
function initClientsMarquee() {
    const marqueeSliders = document.querySelectorAll('.marquee-slider');
    if (marqueeSliders.length === 0) return;

    // Optional: Add pause on hover for accessibility
    marqueeSliders.forEach(slider => {
        const track = slider.querySelector('.marquee-track');
        if (!track) return;

        // Enhanced hover pause effect
        slider.addEventListener('mouseenter', () => {
            track.style.animationPlayState = 'paused';
        });

        slider.addEventListener('mouseleave', () => {
            track.style.animationPlayState = 'running';
        });
    });
}

/**
 * Module 12: Footer Z-Index Control
 * Changes footer z-index when scrolling to bottom of page
 * Only works on desktop resolution (min-width: 1024px)
 */
function initFooterZIndex() {
    const footer = document.querySelector('.site-footer');
    if (!footer) return;

    // Function to check if current viewport is desktop
    function isDesktop() {
        return window.innerWidth >= 1024;
    }

    // Function to check scroll position
    function checkScrollPosition() {
        // Only execute on desktop
        if (!isDesktop()) {
            // Reset footer z-index on mobile/tablet
            footer.style.zIndex = 5;
            return;
        }

        // Calculate total document height
        const docHeight = Math.max(
            document.body.scrollHeight,
            document.documentElement.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.offsetHeight,
            document.body.clientHeight,
            document.documentElement.clientHeight
        );

        // Current scroll position + viewport height
        const scrollPosition = window.scrollY + window.innerHeight;

        // Check if at bottom of page (with small margin)
        if (docHeight - scrollPosition < 5) {
            footer.style.zIndex = '10';
        } else {
            footer.style.zIndex = '-10';
        }
    }

    // Add scroll event listener
    window.addEventListener('scroll', checkScrollPosition);
    
    // Add resize event listener to handle screen size changes
    window.addEventListener('resize', checkScrollPosition);

    // Initial check
    checkScrollPosition();
}

/**
 * Module: Smart Sticky Header
 * Uses different class name to avoid GSAP conflicts
 */
function initStickyHeader() {
    const body = document.body;
    const horizontalSection = document.querySelector('.horizontal-section');
    
    if (!body) return;
    
    let isSticky = false;
    const scrollThreshold = 150; // Threshold más alto para evitar activación accidental
    
    function handleStickyHeader() {
        const scrollY = window.scrollY;
        const shouldBeSticky = scrollY > scrollThreshold;
        
        // Verificar si estamos en el horizontal-section (que tiene su propio comportamiento)
        if (horizontalSection) {
            const rect = horizontalSection.getBoundingClientRect();
            const isInHorizontalSection = rect.top <= 0 && rect.bottom >= 0;
            
            // Si estamos en el horizontal-section, no aplicar sticky header
            if (isInHorizontalSection) {
                if (isSticky) {
                    body.classList.remove('sticky-header-active');
                    isSticky = false;
                }
                return;
            }
        }
        
        // Aplicar o quitar sticky header
        if (shouldBeSticky && !isSticky) {
            body.classList.add('sticky-header-active');
            isSticky = true;
        } else if (!shouldBeSticky && isSticky) {
            body.classList.remove('sticky-header-active');
            isSticky = false;
        }
    }
    
    // Throttled scroll listener para mejor performance
    let ticking = false;
    function onScroll() {
        if (!ticking) {
            requestAnimationFrame(() => {
                handleStickyHeader();
                ticking = false;
            });
            ticking = true;
        }
    }
    
    // Event listeners
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', handleStickyHeader, { passive: true });
    
    // Check inicial después de cargar
    setTimeout(handleStickyHeader, 100);
}

document.addEventListener('DOMContentLoaded', function() {
    // Validación de la existencia de los elementos
    const galleryRows = document.querySelectorAll('.gallery_block .row');
    
    // Solo ejecutar si encontramos al menos una galería
    if (galleryRows.length > 0) {
        galleryRows.forEach(row => {
            const galleryItems = row.querySelectorAll('.gallery-item');
            const totalItems = galleryItems.length;
            
            // Solo aplicamos los estilos si hay múltiples elementos
            if (totalItems > 1) {
                row.style.setProperty('--total-items', totalItems);
                
                // Opcionalmente, añadir clase para CSS
                row.classList.add('multiple-items');
            } else if (totalItems === 1) {
                // Asegurarnos que el único elemento tenga la clase correcta
                galleryItems[0].classList.add('full-width');
                galleryItems[0].classList.remove('shared-width');
            }
        });
    }
});

/**
 * «Mostrar más» del listado de proyectos (sólo móvil).
 *
 * Los proyectos ya vienen todos en el HTML: el botón se limita a marcar la
 * sección como desplegada para que el CSS deje de ocultar los que sobran.
 * La clase `js-recorta-proyectos` la pone este script, de modo que sin JS no
 * se oculta nada y el listado sigue completo.
 */
document.addEventListener('DOMContentLoaded', function () {
    const boton = document.querySelector('.projects-show-more');

    if (!boton) {
        return;
    }

    const seccion = boton.closest('.horizontal-section');

    if (!seccion) {
        return;
    }

    const visibles = parseInt(boton.dataset.visibles, 10) || 6;
    const paneles = seccion.querySelectorAll('.panel');
    const ocultos = Array.prototype.slice.call(paneles, visibles);

    ocultos.forEach(function (panel) {
        panel.classList.add('panel--oculto');
    });

    boton.addEventListener('click', function () {
        seccion.classList.add('proyectos-desplegados');
        boton.hidden = true;

        // El foco estaba en un botón que acaba de desaparecer: se lleva al
        // primer proyecto revelado para no perder el hilo con el teclado.
        const enlace = ocultos[0] && ocultos[0].querySelector('a');

        if (enlace) {
            enlace.focus({ preventScroll: true });
        }
    });
});
